import { createApi } from '@reduxjs/toolkit/query/react';
import { createSentryFetchBaseQuery } from '../../../../app/api/sentryFetchBaseQuery';
import { getClientCredentials } from '../../../../app/shared/utils/cookie.utils';

interface GenerateStdJsonResponse {
  message: string;
  project_id?: string;
}

interface GenerateAuditReportRequest {
  projectId: string;
  userId?: string;
  reviewer_comment?: string;
}

interface UpdateProjectRequest {
  projectId: string;
  userId: string;
  title?: string;
  domain?: string;
  overview?: string;
  environment?: string;
  review_status?: string;
}

interface UpdateProjectResponse {
  message: string;
  projectId: string;
}

interface AuditReportResponse {
  audit_id: string;
  project_id: string;
  project_name: string;
  environment: string;
  audit_timestamp_local: string;
  audit_timestamp_utc: string;
  reviewer: {
    name: string;
    verdict: string;
    comments: string;
  };
  scope_of_analysis: {
    ml_units_evaluated: number;
    llm_units_evaluated: number;
  };
  kpis_dimension_level: {
    ethical_behavior_score: number;
    ai_safety_risk_pct: number;
    governance_coverage_pct: number;
    internal_violations_count: number;
    external_violations_count: number;
  };
  controls: {
    owasp_controls: Array<{
      name: string;
      status: string;
      severity: string;
      category: string;
      reference: string;
    }>;
    ontology_signals: Array<{
      domain?: string;
      risk_domain?: string;
      risk_level: string;
    }>;
    internal_compliance: Array<{
      dimension: string;
      issue_type: string;
      severity: string;
      status: string;
      evidence: { [key: string]: string };
      provenance: string;
      internal_clause: string | null;
      reason: string;
    }>;
    external_compliance: Array<{
      dimension: string;
      issue_type: string;
      feature: string;
      evidence: string;
      status: string;
      matches: Array<{
        regulation: string;
        article: string;
        obligation: string;
        distance: number;
        reason: string;
      }>;
    }>;
  };
  risk_and_recommendations_advisory: string;
  final_verdict_reviewer_only: {
    verdict: string;
    rationale_verbatim: string;
  };
  full_text_report: string;
  ocaf_anchor?: {
    ocaf_sha256_hash: string;
    status?: string;
  };
}

export interface Analysis360Response {
  projectId: string;
  environment: string;
  aggregated_results: {
    meaid: string;
    /** PDC+ run object when 360 aggregation includes the new schema. */
    prompt_injection?: Record<string, unknown>;
    detailed_360_data: {
      bias?: Record<string, any>;
      fairness?: {
        ML_Fairness_Metric?: any[];
        LLM_Fairness_Metric?: any[];
      };
      explainability?: {
        ML_Model_Explainability?: Record<string, any>;
        LLM_Model_Explainability?: Record<string, any>;
      };
      ai_safety?: {
        risk_score?: number;
        risk_tier?: string;
        review_status?: string;
        dataset_verdict?: string;
        input_type?: string;
        score_breakdown?: Record<string, unknown>;
        evidence?: Array<Record<string, unknown>>;
        developer_guidance?: Record<string, unknown>;
        ML_ABUSE_DETECTION?: any[];
        Overall_Abuse_Score?: number;
        LLM_ABUSE_ANALYSIS?: Record<string, any>;
      };
      abuse_ai?: {
        ML_ABUSE_DETECTION?: any[];
        Overall_Abuse_Score?: number;
        LLM_ABUSE_ANALYSIS?: Record<string, any>;
      };
      prompt_injection?: {
        ML_PROMPT_INJECTION_ANALYSIS?: Record<string, any>;
        LLM_PROMPT_INJECTION_ANALYSIS?: Record<string, any>;
      };
    };
  };
}

// AI Assurance 360° — POST /project/features/ai-assurance
export interface FactAiAssurance360Row {
  review_id?: string;
  account_id?: string;
  project_id?: string;
  review_timestamp_utc?: string;
  ingestion_timestamp_utc?: string;
  period_label?: string;
  reviewer_name?: string;
  reviewer_role?: string;
  approval_status?: string;
  is_approved?: boolean;
  is_conditional?: boolean;
  is_rejected?: boolean;
  ethical_behavior_score?: number;
  ai_safety_risk_pct?: number;
  governance_coverage_pct?: number;
  internal_violations_count?: number;
  external_violations_count?: number;
  owasp_overall_status?: string;
  owasp_fail_count?: number;
  ontology_overall_risk?: string;
  highest_domain_risk?: string;
  bias_risk_level?: string | null;
  fairness_risk_level?: string | null;
  explainability_risk_level?: string | null;
  abuse_risk_level?: string | null;
  prompt_injection_risk_level?: string | null;
  security_risk_level?: string | null;
  is_high_risk?: boolean;
  is_medium_risk?: boolean;
  is_low_risk?: boolean;
  [key: string]: unknown;
}

export interface AiAssuranceRequest {
  projectId?: string;
  accountId?: string;
  periodLabel?: string;
  latestOnly?: boolean;
}

export interface AiAssuranceResponse {
  status: number;
  message: string;
  count: number;
  postgres_inserted?: number;
  fact_ai_assurance_360: FactAiAssurance360Row[];
}

/** GET /project/features/llm-analysis/results */
export interface LlmAnalysisBreakdownBucket {
  avg_score: number | null;
  count: number;
  severity: string | null;
}

export interface LlmAnalysisResultRow {
  id: number;
  analysis_type: string;
  created_at: string;
  domain: string;
  environment: string;
  is_rerun: boolean;
  meaid: string;
  project_id: string;
  raw_payload: Record<string, unknown>;
  score: string | null;
  severity: string | null;
  user_id: string;
}

export interface LlmAnalysisResultsResponse {
  status: number;
  total: number;
  limit: number;
  offset: number;
  project_id: string;
  results: LlmAnalysisResultRow[];
  breakdown?: Record<string, LlmAnalysisBreakdownBucket[]>;
}

export interface LlmAnalysisResultsQueryParams {
  limit?: number;
  offset?: number;
  project_id?: string;
}

export const LLM_ANALYSIS_PAGE_SIZE_MAX = 200;

// Define the base API slice
export const projectApi = createApi({
  reducerPath: 'projectApi',
  baseQuery: createSentryFetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_API_BASE_URL}/project/`,
    prepareHeaders: headers => {
      // Add authorization token
      const token = localStorage.getItem('accessToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      // Add client credentials based on selected environment
      const environment = (localStorage.getItem('SELECTED_ENV') || 'DEV') as 'DEV' | 'PROD';
      const credentials = getClientCredentials(environment);
      const userId = localStorage.getItem('userId');
      if (credentials) {
        headers.set('X-Client-Id', credentials.clientId);
        if (credentials.clientSecret) {
          headers.set('X-Client-Secret', credentials.clientSecret);
        }
      }
      if (userId) {
        headers.set('X-User-Id', userId);
      }

      return headers;
    }
  }),
  endpoints: builder => ({
    getProjectResults: builder.query({
      query: (projectId: string) => `features/${projectId}/results`,
      // Add caching configuration
      keepUnusedDataFor: 300 // Keep data for 5 minutes
    }),

    // Add project list query
    getProjectLists: builder.query({
      query: ({
        userId,
        environment,
        organizationId
      }: {
        userId: string;
        environment?: string;
        organizationId?: string;
      }) => {
        const query = new URLSearchParams();
        if (environment) {
          query.set('environment', environment);
        }
        if (organizationId) {
          query.set('organizationId', organizationId);
        }
        const queryString = query.toString();
        const url = queryString ? `${userId}/all-projects?${queryString}` : `${userId}/all-projects`;
        console.log('getProjectLists API call - URL:', url);
        return url;
      },
      // Add caching configuration
      keepUnusedDataFor: 60 // Keep data for 1 minute
    }),

    // Existing mutation
    generateStandardizedProjectJson: builder.mutation<GenerateStdJsonResponse, { projectId: string; userId: string }>({
      query: body => ({
        url: 'features/generate-std-prj-json',
        method: 'POST',
        body
      })
    }),

    // ✅ New mutation: Generate Audit Report
    generateAuditReport: builder.mutation<AuditReportResponse, GenerateAuditReportRequest>({
      query: body => ({
        url: 'features/audit-report',
        method: 'POST',
        body
      })
    }),

    // ✅ Update Project
    updateProject: builder.mutation<UpdateProjectResponse, UpdateProjectRequest>({
      query: body => ({
        url: 'update-project',
        method: 'PUT',
        body
      })
    }),

    // ✅ OWASP Controls Generator
    generateOwaspControls: builder.mutation<any, { projectId: string; userId: string }>({
      query: body => ({
        url: 'features/owasp',
        method: 'POST',
        body
      })
    }),

    // ✅ Ontology Generator
    generateOntology: builder.mutation<any, { projectId: string; userId: string }>({
      query: body => ({
        url: `${process.env.REACT_APP_API_BASE_URL}/ontology/project/features/ontology`,
        method: 'POST',
        body
      })
    }),

    // ✅ Get Whitelist Rules
    getWhitelistRules: builder.query<any, string | undefined>({
      query: domain => (domain ? `utils/whitelist_rules?domain=${encodeURIComponent(domain)}` : 'utils/whitelist_rules')
    }),

    // ✅ AI Assurance 360° — extract from collection_reviewer_results, upsert Postgres, return flattened records
    postAiAssurance: builder.mutation<AiAssuranceResponse, AiAssuranceRequest>({
      query: body => ({
        url: 'features/ai-assurance',
        method: 'POST',
        body
      })
    }),

    // ✅ 360 Analysis — GET /project/features/{projectId}/360-analysis
    get360Analysis: builder.query<Analysis360Response, string>({
      query: (projectId: string) => `features/${projectId}/360-analysis`,
      keepUnusedDataFor: 0 // Always refetch on tab activation
    }),

    getLlmAnalysisResults: builder.query<LlmAnalysisResultsResponse, LlmAnalysisResultsQueryParams | void>({
      query: params => ({
        url: 'features/llm-analysis/results',
        params: {
          limit: Math.min(params?.limit ?? 50, LLM_ANALYSIS_PAGE_SIZE_MAX),
          offset: params?.offset ?? 0,
          ...(params?.project_id ? { project_id: params.project_id } : {})
        }
      }),
      keepUnusedDataFor: 60
    })
  })
});

// Export the hooks
export const {
  useGetProjectResultsQuery,
  useGetProjectListsQuery,
  useGenerateStandardizedProjectJsonMutation,
  useGenerateAuditReportMutation,
  useUpdateProjectMutation,
  useGenerateOwaspControlsMutation,
  useGenerateOntologyMutation,
  useGetWhitelistRulesQuery,
  usePostAiAssuranceMutation,
  useGet360AnalysisQuery,
  useGetLlmAnalysisResultsQuery
} = projectApi;
